import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBracketsCurly } from "@fortawesome/pro-solid-svg-icons";
import { JSONTree } from "react-json-tree";
import Skeleton from "react-loading-skeleton";
import { DateTime } from "luxon";
import Typography from "core/components/Typography";
import Pill from "core/components/Pill";
import CopyButton from "core/components/CopyButton";
import DownloadButton from "core/components/DownloadButton";
import {
  Panel,
  PanelDescription,
  PanelHeader,
  PanelTitle,
} from "core/components/Panel";
import {
  DEPLOYMENT_STATUSES,
  DEPLOYMENT_STATUS_MAP,
  DEPLOYMENT_STATUS_PILL_VARIANTS,
  jsonTheme,
  tooltipId,
  dateTimeFormat,
} from "./constants";
import {
  StyledLabel,
  StyledValue,
  Header,
  ComboButton,
  PanelContent,
} from "./styles";
import ImportLicenseForm from "./ImportLicenseForm";

const jsonTreeSettings = {
  getItemString: (type, data, itemType, itemString) => {
    return <span> {itemType}</span>;
  },
  labelRenderer: (keyPath, nodeType, expanded, expandable) => {
    if (keyPath[0] === "root") {
      return <StyledLabel root>licenses:</StyledLabel>;
    }
    return <StyledLabel>{keyPath[0]}:</StyledLabel>;
  },
  valueRenderer: (valueAsString, value, keyPath) => {
    return <StyledValue>{valueAsString}</StyledValue>;
  },
  shouldExpandNodeInitially: (keyPath, data, level) => {
    return level <= 3;
  },
  collectionLimit: 15,
  theme: jsonTheme,
  invertTheme: true,
};

const rootTitle = () => {
  const pathName = window.location.pathname;
  return pathName.includes("/confirm")
    ? "Deployment Review"
    : "Product Licenses";
};

const ProductLicenseHeader = ({
  licenseDeployment,
  customerSegment,
  customerSegmentName,
  isLicensePanelOpen,
  setIsLicensePanelOpen,
  deploymentInProgressTime,
  productLicenses,
  getPreparedLicenses,
  importLicense,
}) => {
  const location = useLocation();
  const [toolsTab, setToolsTab] = useState(null);

  const locationIsMain = useMemo(
    () =>
      location.pathname
        .split("/")
        .filter((p) => !!p)
        .reverse()[0] === "product",
    [location]
  );

  const locationIsProductPath = useMemo(
    () =>
      location.pathname
        .split("/")
        .filter((p) => !!p)
        .reverse()[2] === "product",
    [location]
  );

  const locationContainsProductPath = useMemo(
    () => location.pathname.includes("/product/"),
    [location]
  );

  const renderDeploymentCounterText = (deployment) => {
    const { status: statusText } = deployment;

    return (
      <span>
        <span style={{ marginRight: "4px" }}>{statusText}:</span>
        <span style={{ fontFamily: "'Roboto Mono', monospace" }}>
          {deploymentInProgressTime}
        </span>
      </span>
    );
  };

  const handleImportSubmit = (importedLicense) => {
    importLicense(importedLicense);
  };

  return (
    <>
      <Panel
        isOpen={isLicensePanelOpen}
        onOpenChange={(isOpen) => setIsLicensePanelOpen(isOpen)}
      >
        <PanelHeader>
          <PanelTitle>Admin Tools</PanelTitle>
          <PanelDescription>
            Review the underlying data structure for draft product license
            changes, compare to the server state, and import in JSON license for
            quick updates between environments.
          </PanelDescription>
        </PanelHeader>
        <PanelContent>
          <div style={{ display: "flex", alignItems: "center" }}>
            <ComboButton
              isActive={!toolsTab || toolsTab === "draft"}
              onClick={() => setToolsTab("draft")}
            >
              Draft
            </ComboButton>
            <ComboButton
              isActive={toolsTab === "saved"}
              onClick={() => setToolsTab("saved")}
            >
              Saved
            </ComboButton>
            <ComboButton
              isActive={toolsTab === "import"}
              onClick={() => setToolsTab("import")}
            >
              Import
            </ComboButton>

            {toolsTab === "saved" && (
              <div style={{ marginLeft: "5px" }}>
                <CopyButton
                  value={JSON.stringify(productLicenses)}
                  hoverTitle="Copy Config"
                  tooltipDirection="top"
                />
                <DownloadButton
                  value={productLicenses}
                  hoverTitle="Download Config"
                  tooltipDirection="top"
                />
              </div>
            )}
          </div>

          {!toolsTab || toolsTab === "draft" ? (
            <JSONTree
              data={getPreparedLicenses() || "No changes"}
              {...jsonTreeSettings}
            />
          ) : null}

          {toolsTab === "saved" ? (
            <div>
              <JSONTree
                data={productLicenses || "Not Configured"}
                {...jsonTreeSettings}
              />
            </div>
          ) : null}
          {toolsTab === "import" ? (
            <div style={{ marginTop: "10px" }}>
              <ImportLicenseForm onSubmit={handleImportSubmit} />
            </div>
          ) : null}
        </PanelContent>
      </Panel>

      <Header
        style={{
          display: "block",
          borderBottom: locationIsProductPath && "1px solid #000000",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h2" noMargin>
              {rootTitle()}
            </Typography>
            <div
              className="panel__trigger"
              onClick={() => setIsLicensePanelOpen(true)}
              style={{ cursor: "pointer" }}
            >
              <FontAwesomeIcon
                icon={faBracketsCurly}
                style={{
                  background: "#6842c0",
                  color: "#fff",
                  border: "1px solid #6842c0",
                  borderRadius: "999px",
                  padding: "4px",
                }}
              />
            </div>
          </div>
          {(locationIsMain || locationContainsProductPath) && (
            <div>
              {licenseDeployment ? (
                <Pill
                  textDataTest="licenseStatus"
                  size="large"
                  variant={
                    DEPLOYMENT_STATUS_PILL_VARIANTS[
                      DEPLOYMENT_STATUS_MAP[licenseDeployment.rawStatus]
                    ]
                  }
                  tooltipId={tooltipId}
                  tooltipContent={
                    ["succeeded", "timeout", "failed"].includes(
                      licenseDeployment.rawStatus
                    )
                      ? licenseDeployment.tooltip
                      : null
                  }
                >
                  {licenseDeployment?.status === DEPLOYMENT_STATUSES.IN_PROGRESS
                    ? renderDeploymentCounterText(licenseDeployment)
                    : licenseDeployment.status}
                </Pill>
              ) : (
                <Skeleton style={{ width: "50px", height: "30px" }} />
              )}
            </div>
          )}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "10px",
            marginTop: "5px",
            marginBottom: "15px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            {customerSegmentName && (
              <Pill text={customerSegmentName} textDataTest="customerSegment" />
            )}
            {customerSegment?.medicareLicensed && (
              <Pill
                text={customerSegment?.medicareLicensed && "Medicare QE"}
                textDataTest="medicareLicensed"
              />
            )}
            {customerSegment?.isPrOrg && (
              <Pill
                text={`PR #${customerSegment.prNumber}`}
                textDataTest="isPrOrg"
              />
            )}
          </div>
          <div
            style={{
              marginBottom: "0px",
              fontSize: "14px",
              fontFamily: "Inter",
            }}
          >
            {(locationIsMain || locationContainsProductPath) &&
              licenseDeployment?.licenseLastSuccessfulDeployment &&
              `Last successful deployment: ${licenseDeployment.licenseLastSuccessfulDeployment.toFormat(
                dateTimeFormat
              )}`}
          </div>
        </div>
      </Header>
    </>
  );
};

export default ProductLicenseHeader;
